import SecurityCenter from "@/icons/login/SecurityCenter";
import Facebook from "@/icons/login/Facebook";
import Google from "@/icons/login/Google";
import Naver from "@/icons/login/Naver";
import Apple from "@/icons/login/Apple";
import LoginInput from "@/components/input/LoginInput";
import { Button } from "@/components/ui/button";
import { MouseEvent, useEffect, useRef, useState } from "react";
import { useMounted } from "@/hooks/useMounted";
import { getAccessCode, getAuthLevel, getCookies, getFallbackUrl, getRedirectUrl, getSearchParams, handleEnterKey } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import ErrorTooltip from "@/components/tooltip/ErrorTooltip";
import Popover from "@/components/popover/Popover";
import { useFailure } from "@/hooks/useFailure";
import ButtonSpinner from "@/components/spinner/ButtonSpinner";
import CookieStore from "@/stores/CookieStore";
import { NX_LATC_COOKIE } from "@/consts/cookies";
import { isIE, isMobile } from "react-device-detect";
import { sendClickLog, sendContentsLog, withLog } from "@/lib/a2s";
import {
  AUTHLEVEL_SEARCH_PARAM,
  AUTHLEVEL_SEARCH_PARAM_CAMEL_CASE,
  DEFAULT_MOBILE_JOIN_ACCESS_CODE,
  DEFAULT_PC_JOIN_ACCESS_CODE,
  ERROR_CODE_SEARCH_PARAM,
} from "@/consts";
import { ErrorCodeType } from "@/types";
import { getIsGetoAccessCode } from "@/lib/geto";
import { getIsStellaAccessCode } from "@/lib/stella";

const IDPasswordPanel = () => {
  const passwordRef = useRef<HTMLInputElement>(null);

  const cookieRecentLoginInfo = getCookies(NX_LATC_COOKIE);
  const isCafe = CookieStore((state) => state.isCafe);
  const signup = CookieStore((state) => state.signup);
  const usetpa = CookieStore((state) => state.usetpa);

  const turnstileRef = useRef(null);
  const mounted = useMounted();

  const [buttonLoading, setButtonLoading] = useState(false);
  const [showTurnstile, setShowTurnstile] = useState(getSearchParams(ERROR_CODE_SEARCH_PARAM) === "need_captcha");
  const [id, setId] = useState(window.__GLOBAL_DATA__?.userId ?? "");
  const [password, setPassword] = useState("");
  const [invaildId, setInvalidId] = useState(false);
  const [invaildPassword, setInvalidPassword] = useState(false);

  const [checkTurnstile, setCheckTurnstile] = useState(false);
  const [token, setToken] = useState("");
  const [isKeepLogin, setIsKeepLogin] = useState(false);
  const [errorCode, setErrorCode] = useState<ErrorCodeType | undefined>();

  const { handleRedirectFailure } = useFailure();
  const isGeto = getIsGetoAccessCode();
  const isStella = getIsStellaAccessCode();
  // PC방·스텔라 진입: 로그인 상태 유지·최근 로그인 숨김
  const hideKeepLogin = isCafe || isStella;
  // GETO·스텔라 진입: 보안센터·ID/PW찾기 GNB 숨김
  const hideExitLinks = isGeto || isStella;

  const signIn = (captchaLoginToken?: string) => {
    sendClickLog("LOGIN0034");
    setButtonLoading(true);
    nest.signIn({
      id: id,
      password: password,
      keepMeSignIn: isKeepLogin,
      captchaLoginToken: captchaLoginToken ? captchaLoginToken : undefined,
      accessCode: getAccessCode(),
      redirectUrl: getRedirectUrl(),
      fallbackUrl: getFallbackUrl(),
      failure: (error) => {
        const { code } = error;

        if (code === "need_captcha") {
          setShowTurnstile(true);
        } else if (
          code === "blocked_international_signin" ||
          code === "request_parent_agreement" ||
          code === "withdrawal_grace_period" ||
          code === "protected_by_nass"
        ) {
          handleRedirectFailure(error, { setErrorCode, accesscode: getAccessCode(), keepLogin: isKeepLogin ? 1 : 0 });
        } else {
          setErrorCode(code);
        }

        setButtonLoading(false);
      },
    });
  };

  const handleIdChange = (value: string) => {
    if (invaildId) {
      setInvalidId(false);
    }
    setId(value);
  };

  const handlePasswordChange = (value: string) => {
    if (invaildPassword) {
      setInvalidPassword(false);
    }
    setPassword(value);
  };

  const handleKeepLoginChange = (checked: boolean) => {
    setIsKeepLogin(checked);
  };

  const handleLoginClick = () => {
    if (id.trim().length === 0) {
      setInvalidId(true);
      return;
    } else if (password.trim().length === 0) {
      setInvalidPassword(true);
      return;
    }

    if (showTurnstile) {
      if (!checkTurnstile) {
        setErrorCode("fe_custom_turnstile_uncheck_error");
        return;
      }

      if (!token) {
        setErrorCode("fe_custom_turnstile_token_error");
        return;
      }

      nest.verifyTurnstileCaptcha({
        id: id,
        captchaToken: token,
        success(response) {
          const { captchaLoginToken } = response;
          signIn(captchaLoginToken);
        },
        failure: (error) => {
          const { code } = error;

          setErrorCode(code);
        },
      });
    } else {
      signIn();
    }
  };

  const handleSignupClick = () => {
    const isEasySignUp = signup;

    if (isEasySignUp === 1) {
      document.location.href = import.meta.env.VITE_EASY_SIGN_UP_URL;
    } else {
      let accessCode = getAccessCode();
      if (accessCode === 0) {
        accessCode = isMobile ? DEFAULT_MOBILE_JOIN_ACCESS_CODE : DEFAULT_PC_JOIN_ACCESS_CODE; // 모바일 환경에서 기본 액세스 코드, PC 환경에서 기본 액세스 코드
      }
      NXMember.GoJoin({
        accesscode: accessCode,
        redirect: getRedirectUrl(),
        authlevel: getAuthLevel() === 1 ? 1 : undefined, // ATL 2.0일 경우 기본 값인 undefined 처리
        usetpa: isMobile ? 1 : undefined, // 모바일 환경에서 TPA 사용 여부
      });
    }
  };

  const handleFindIdClick = () => {
    const showGnb = hideExitLinks ? 0 : 1;
    NXMember.GoFindID({ redirect: document.location.href, pagetype: 3, showgnb: showGnb });
  };

  const handleFindPasswordClick = () => {
    const showGnb = hideExitLinks ? 0 : 1;
    NXMember.GoFindPwd({ redirect: document.location.href, pagetype: 3, showgnb: showGnb });
  };

  const handleSecurityCenterClick = () => {
    document.location.href = import.meta.env.VITE_SECURITY_CENTER_URL;
  };

  const isOldIE = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return isIE && (document as any).documentMode && (document as any).documentMode <= 10;
  };

  const handleTpaLoginClick = (e: MouseEvent<HTMLButtonElement>) => {
    const code = Number(e.currentTarget.dataset.code);

    // apple
    if (code === 4 && isOldIE()) {
      setErrorCode("fe_custom_old_ie_error");
      return;
    }

    const authLevelString = getSearchParams(AUTHLEVEL_SEARCH_PARAM) ?? getSearchParams(AUTHLEVEL_SEARCH_PARAM_CAMEL_CASE);
    let authLevel: undefined | number;
    if (authLevelString === "1" || authLevelString === "2") {
      authLevel = Number(authLevelString);
    }

    nest.signInTpa({
      tpaCode: code,
      accessCode: getAccessCode(),
      redirectUrl: getRedirectUrl(),
      fallbackUrl: getFallbackUrl(),
      tpaSignUpLevel: authLevel,
    });
  };

  useEffect(() => {
    if (window.__GLOBAL_DATA__?.userId) {
      passwordRef.current?.focus();
    }
  }, []);

  useEffect(() => {
    sendContentsLog("LOGIN0001");
  }, []);

  // 뒤로가기 시 buttonLoading이 남아있는 문제 수정 (bfcache 추정)
  useEffect(() => {
    const handlePageShow = () => {
      setButtonLoading(false);
    };
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  useEffect(() => {
    if (!mounted) {
      return;
    }

    if (!showTurnstile) {
      return;
    } else {
      // turnstile 노출 시 로그
      sendContentsLog("LOGIN0030");
    }

    // Turnstile이 로드됐는지 체크
    if (window.turnstile && turnstileRef.current) {
      window.turnstile.render(turnstileRef.current, {
        sitekey: import.meta.env.VITE_TURNSTILE_SITE_KEY,
        callback: withLog("LOGIN0031", (token: string) => {
          setCheckTurnstile(true);
          setToken(token);
        }),
      });
    }
  }, [mounted, showTurnstile]);

  return (
    <div className="w-full">
      <div className="mt-8 space-y-3 md:mt-10">
        <ErrorTooltip errorCode={errorCode} />
        <LoginInput
          value={id}
          setValue={handleIdChange}
          className="block"
          isError={invaildId}
          errorMessage="넥슨ID(아이디 또는 이메일)를 입력해주세요."
          onKeyDown={(e) => handleEnterKey(e, handleLoginClick)}
        />
        <LoginInput
          ref={passwordRef}
          value={password}
          setValue={handlePasswordChange}
          className="block"
          type="password"
          isError={invaildPassword}
          errorMessage="비밀번호를 입력해주세요."
          onKeyDown={(e) => handleEnterKey(e, handleLoginClick)}
        />
        {!hideKeepLogin && (
          <div className="flex items-center space-x-2">
            <Checkbox id="saveid" className="size-4" checked={isKeepLogin} onCheckedChange={withLog("LOGIN0006", handleKeepLoginChange)} />
            <label htmlFor="saveid" className="text-xs leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              로그인 상태 유지
            </label>
          </div>
        )}
        {showTurnstile && <div ref={turnstileRef} className="w-full" data-size="flexible"></div>}
      </div>

      <Button
        variant={"black"}
        className="mb-2 mt-6 block md:mt-8"
        disabled={buttonLoading}
        onClick={withLog("LOGIN0007", handleLoginClick)}
      >
        <span className="relative">
          <ButtonSpinner loading={buttonLoading} />
          넥슨 ID 로그인
          <Popover position="right" hidden={hideKeepLogin || cookieRecentLoginInfo !== "0"} />
        </span>
      </Button>

      <div className="grid grid-cols-2 items-center">
        <div className="flex space-x-3">
          {/* 스텔라 진입: 회원가입 숨김 */}
          {!isStella && (
            <Button variant={"link"} size={"fit"} className="whitespace-nowrap" onClick={withLog("LOGIN0008", handleSignupClick)}>
              회원가입
            </Button>
          )}
          {/* 스텔라 진입: ID/PW 찾기 숨김 */}
          {!isStella && (
            <>
              <Button variant={"link"} size={"fit"} className="whitespace-nowrap" onClick={withLog("LOGIN0009", handleFindIdClick)}>
                넥슨ID 찾기
              </Button>
              <Button variant={"link"} size={"fit"} className="whitespace-nowrap" onClick={withLog("LOGIN0010", handleFindPasswordClick)}>
                비밀번호 찾기
              </Button>
            </>
          )}
        </div>
        {!hideExitLinks && (
          <Button
            variant={"link"}
            size={"fit"}
            className="gap-0 space-x-0.5 justify-self-end font-medium text-blue-600 [&_svg]:size-4"
            onClick={withLog("LOGIN0011", handleSecurityCenterClick)}
          >
            <SecurityCenter />
            보안센터
          </Button>
        )}
      </div>

      {usetpa !== 0 && (
        <div>
          <div className="mt-8 flex items-center space-x-6 md:mt-10">
            <div className="h-px grow bg-gray-300" />
            <p className="font-medium text-gray-600">또는</p>
            <div className="h-px grow bg-gray-300" />
          </div>
          <div>
            <p className="my-5 text-center font-bold">다른 계정으로 로그인</p>
            <div className="flex w-full justify-center space-x-5">
              <div className="relative">
                <Button variant={"icon"} data-code="1" className="block size-14 p-0" onClick={withLog("LOGIN0012", handleTpaLoginClick)}>
                  <Facebook />
                </Button>
                <Popover hidden={hideKeepLogin || cookieRecentLoginInfo !== "1"} />
              </div>
              <div className="relative">
                <Button variant={"icon"} data-code="2" className="block size-14 p-0" onClick={withLog("LOGIN0013", handleTpaLoginClick)}>
                  <Google />
                </Button>
                <Popover hidden={hideKeepLogin || cookieRecentLoginInfo !== "2"} />
              </div>
              <div className="relative">
                <Button variant={"icon"} data-code="3" className="block size-14 p-0" onClick={withLog("LOGIN0014", handleTpaLoginClick)}>
                  <Naver />
                </Button>
                <Popover hidden={hideKeepLogin || cookieRecentLoginInfo !== "3"} />
              </div>
              <div className="relative">
                <Button variant={"icon"} data-code="4" className="block size-14 p-0" onClick={withLog("LOGIN0015", handleTpaLoginClick)}>
                  <Apple />
                </Button>
                <Popover hidden={hideKeepLogin || cookieRecentLoginInfo !== "4"} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IDPasswordPanel;
